import React, { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import {
  Box,
  Button,
  Checkbox,
  FilePreviewList,
  FileUpload,
  Flex,
  FormMessage,
  Input,
  Label,
  Link,
  Panel,
  Radio,
  Text,
  theme,
} from '@xometry/xometry_loft';
import TooltipBlueIcon from '../../../../components/TooltipBlueIcon';
import BillingContact from './BillingContact';
import BusinessInformation from './BusinessInformation';
import { Formik } from 'formik';
import { useMutation, ApolloClient, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { createUploadLink } from 'apollo-upload-client';
import {
  CreateCreditApplicationQuery,
  UploadPaymentFileQuery,
  PaymentFileS3Bucket,
  BillingAccount,
} from '../../../../schema/generated/schema';
import {
  creditApplicationValiationSchema,
  creditApplicationInitialValues,
} from './formikConstants';
import { PAYMENT_FILE_ORIGIN } from '../../../../config';
import { returnNonEmptyCopiedObject } from '../../../../utils/helpers';
import DecisioningStates from '../DecisioningStates';

interface Props {
  userId: string;
  isStaff: boolean;
  billingAccount: BillingAccount;
}

const CreditTermsForm = ({ userId, isStaff, billingAccount }: Props) => {
  const [fileList, setFileList] = useState([]);
  const [initialValues, setInitialValues] = useState(
    creditApplicationInitialValues
  );
  const [currentInput, setCurrentInput] = useState('');
  const [formDecisionState, setFormDecisionState] = useState({
    decision: 'none',
    email: '',
    terms: { netTerms: '', approvedLimit: 0 },
  });

  const [billingContactEditMode, setBillingContactEditMode] =
    useState<boolean>(false);
  const [businessInformationEditMode, setBusinessInformationEditMode] =
    useState<boolean>(false);

  useEffect(() => {
    if (billingAccount?.id) {
      setInitialValues({
        ...initialValues,
        street1: billingAccount?.contact?.address?.street1,
        street2: billingAccount?.contact?.address?.street2,
        city: billingAccount?.contact?.address?.city,
        state: billingAccount?.contact?.address?.state,
        zipcode: billingAccount?.contact?.address?.zipcode,
        entityName: billingAccount.name,
        acctsPayablePhone: billingAccount?.contact?.phoneNumber,
        acctsPayableEmail: billingAccount?.contact?.emailAddress,
        billingAccountId: billingAccount.id,
      });
    }
  }, []);

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        'apollo-require-preflight': true,
      },
    };
  });

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: authLink.concat(
      createUploadLink({
        uri: `${PAYMENT_FILE_ORIGIN}/graphql/`,
        credentials: 'include',
      })
    ),
  });

  const [createCreditApplication] = useMutation(CreateCreditApplicationQuery);

  // function for createCreditApplication
  const createCreditApplicationHelper = (valuesSanitized, values, res?) => {
    // Check and append file ids to our form values if the exist
    if (res) {
      valuesSanitized = { ...valuesSanitized, files: res };
    }

    // Default for net terms is 'Net 30'; when a user selects the 'other' net terms option, we will concatenate the inputted numerical
    // value to send to our billing-account-service to match what is expected (i.e. 'Net 60', 'Net 90', etc).
    if (valuesSanitized.requestedTermLimit === 'Other') {
      valuesSanitized.requestedTermLimit = `Net ${valuesSanitized.otherRequestedTermLimit}`;
      delete valuesSanitized.otherRequestedTermLimit;
    }

    return createCreditApplication({
      variables: {
        input: {
          ...valuesSanitized,
          // Formik changes radio buttons value to always be a string. This check ensures that if the radio button
          // is set to 'true' that boolean true gets passed to the API, and that 'false' gets passed back as boolean false.
          multiplePurchaseOrders: values.multiplePurchaseOrders === 'true',
          // change the values for money into cents
          requestedCreditLimit: Math.round(
            100 * Number(valuesSanitized.requestedCreditLimit)
          ),
          // purchaseOrderAmount is an optional field, so we need to check that it exists
          purchaseOrderAmount: valuesSanitized.purchaseOrderAmount
            ? Math.round(100 * Number(valuesSanitized.purchaseOrderAmount))
            : null,
          userId,
        },
      },
    });
  };

  // Helper function to decide which ui to render for the application decisions
  const decisionHandler = (res) => {
    if (res.data.createCreditApplication.decision === 'Approved') {
      return {
        decision: 'approved',
        terms: {
          netTerms: res.data.createCreditApplication.requestedTermLimit,
          approvedLimit: res.data.createCreditApplication.approvedAmount,
        },
      };
    } else if (res.data.createCreditApplication.decision === 'Pend') {
      return {
        decision: 'pending',
        email: res.data.createCreditApplication.acctsPayableEmail,
      };
    } else {
      return {
        decision: 'pendingDeclined',
        email: res.data.createCreditApplication.acctsPayableEmail,
      };
    }
  };

  return (
    <>
      {formDecisionState.decision !== 'none' && (
        <DecisioningStates
          decision={formDecisionState.decision}
          email={formDecisionState.email}
          terms={formDecisionState.terms}
        />
      )}
      {formDecisionState.decision === 'none' && billingAccount.id && (
        <Panel
          kind="primary"
          width="650px"
          marginRight="24px"
          color={theme.colors.darkBlues[90]}
          paddingTop="0px"
        >
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={creditApplicationValiationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setFormDecisionState({ decision: 'loading' });
              setSubmitting(true);

              // Removes empty strings from optional fields;
              const valuesSanitized: typeof values =
                returnNonEmptyCopiedObject(values);

              // Submitting files is optional; if they are submited, then they need to get uploaded to the s3 bucket
              // before we can submit their id along with the credit application
              if (fileList.length > 0) {
                const filePromises = fileList.map(async (file) => {
                  const res = await client.mutate({
                    mutation: UploadPaymentFileQuery,
                    variables: {
                      file: file.clientFile,
                      bucket: PaymentFileS3Bucket.FinanceDocuments,
                      acls: [{ access: 'read', entity: 'user', id: userId }],
                    },
                  });
                  return { response: res.data.uploadPaymentFile };
                });

                Promise.all(filePromises)
                  .then((value) => {
                    if (window.analytics) {
                      window.analytics.track('Financial Document Uploaded', {
                        staffCreated: isStaff ? 'Yes' : 'No',
                      });
                    }
                    return value.map((res) => {
                      delete res.response.__typename;
                      return res.response;
                    });
                  })
                  .then((res) => {
                    return createCreditApplicationHelper(
                      valuesSanitized,
                      values,
                      res
                    );
                  })
                  .then((res) => {
                    if (window.analytics) {
                      window.analytics.track('Credit Application Submitted', {
                        staffCreated: isStaff ? 'Yes' : 'No',
                      });
                    }
                    const decision = decisionHandler(res);
                    setFormDecisionState(decision);
                    setSubmitting(false);
                  })
                  .catch((e) => {
                    setSubmitting(false);
                    setFormDecisionState({ decision: 'error' });
                    console.error(e);
                  });
              } else {
                return createCreditApplicationHelper(valuesSanitized, values)
                  .then((res) => {
                    if (window.analytics) {
                      window.analytics.track('Credit Application Submitted', {
                        staffCreated: isStaff ? 'Yes' : 'No',
                      });
                    }
                    const decision = decisionHandler(res);
                    setFormDecisionState(decision);
                    setSubmitting(false);
                  })
                  .catch((e) => {
                    console.error(e);
                    setFormDecisionState({ decision: 'error' });
                    setSubmitting(false);
                  });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              // setFieldValue,
            }) => (
              <form
                onSubmit={(e) => {
                  // If errors exist on billing contact or buisiness info, open up that section
                  if (
                    errors.acctsPayablePhone ||
                    errors.acctsPayableEmail ||
                    errors.entityName
                  ) {
                    setBillingContactEditMode(!billingContactEditMode);
                  }
                  if (
                    errors.street1 ||
                    errors.city ||
                    errors.state ||
                    errors.zipcode
                  ) {
                    setBusinessInformationEditMode(
                      !businessInformationEditMode
                    );
                  }
                  // check for value in currentInput and add it to our values for validation before submitting
                  if (currentInput !== '') {
                    values.authorizedEmails = [
                      ...values.authorizedEmails,
                      currentInput,
                    ];
                    setCurrentInput('');
                  }
                  handleSubmit(e);
                }}
              >
                <img
                  src="https://assets.xometry.com/images/Xometry_Blue_Registered%402x.png"
                  alt="Xometry Logo"
                  css={css`
                    max-width: 415px;
                    margin-top: 0px;
                  `}
                />
                <Text kind="panel-title" display="block" marginBottom="28px">
                  Get Terms with Xometry
                </Text>
                {billingAccount && (
                  <>
                    <BillingContact
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      billingContactEditMode={billingContactEditMode}
                      setBillingContactEditMode={setBillingContactEditMode}
                    />
                    <BusinessInformation
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      businessInformationEditMode={businessInformationEditMode}
                      setBusinessInformationEditMode={
                        setBusinessInformationEditMode
                      }
                    />
                  </>
                )}
                {/* Credit Information Section */}
                <Box
                  borderBottom={`1px solid ${theme.colors.darkBlues[25]}`}
                  marginBottom="28px"
                >
                  <Box marginBottom="16px">
                    <Text kind="body-title">Credit Information</Text>
                  </Box>
                  <Flex marginBottom="16px" width="70%">
                    <Box marginRight="8px" width="100%">
                      <Label htmlFor="requestedCreditLimit">
                        Requested Credit Limit
                      </Label>
                      <Input
                        width="100%"
                        step="0.01"
                        type="number"
                        className="pii-encrypt"
                        id="requestedCreditLimit"
                        value={values.requestedCreditLimit}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={Boolean(
                          touched.requestedCreditLimit &&
                            errors.requestedCreditLimit
                        )}
                      />
                      {Boolean(
                        touched.requestedCreditLimit &&
                          errors.requestedCreditLimit
                      ) && (
                        <FormMessage invalid>
                          {errors.requestedCreditLimit}
                        </FormMessage>
                      )}
                    </Box>
                    <Box width="100%">
                      <Label htmlFor="purchaseOrderAmount">
                        <Flex>
                          <Box marginRight="4px">Purchase Order Amount</Box>
                          <Box fontWeight={theme.fontWeights.regular}>
                            <i>Optional</i>
                          </Box>
                        </Flex>
                      </Label>
                      <Input
                        width="100%"
                        step="0.01"
                        type="number"
                        className="pii-encrypt"
                        id="purchaseOrderAmount"
                        value={values.purchaseOrderAmount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={Boolean(
                          touched.purchaseOrderAmount &&
                            errors.purchaseOrderAmount
                        )}
                      />
                      {Boolean(
                        touched.purchaseOrderAmount &&
                          errors.purchaseOrderAmount
                      ) && (
                        <FormMessage invalid>
                          {errors.purchaseOrderAmount}
                        </FormMessage>
                      )}
                    </Box>
                  </Flex>
                  <Box marginBottom="16px" width="70%">
                    <Label htmlFor="requestedTermLimit">
                      <Flex>
                        Requested Term Limit
                        <TooltipBlueIcon>
                          Xometry offers terms at Net 30 by default. Requests
                          for other term limits may take 1-2 days to process.
                        </TooltipBlueIcon>
                      </Flex>
                    </Label>
                    <Flex alignItems="center">
                      <Box marginRight="16px">
                        <Radio
                          scale="small"
                          id="requestedTermLimit"
                          name="requestedTermLimit"
                          value={'Net 30'}
                          checked={values.requestedTermLimit === 'Net 30'}
                          onChange={handleChange}
                        />
                        <label htmlFor="Net 30">Net 30</label>
                      </Box>
                      <Box marginRight="4px">
                        <Radio
                          scale="small"
                          id="requestedTermLimit"
                          name="requestedTermLimit"
                          value={'Other'}
                          checked={values.requestedTermLimit === 'Other'}
                          onChange={handleChange}
                        />
                        <label htmlFor="Other">Other: Net</label>
                      </Box>
                      <Input
                        width="60px"
                        step="1"
                        type="number"
                        id="otherRequestedTermLimit"
                        value={
                          values.requestedTermLimit === 'Other'
                            ? values.otherRequestedTermLimit
                            : ''
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        readOnly={values.requestedTermLimit === 'Net 30'}
                        invalid={Boolean(
                          touched.otherRequestedTermLimit &&
                            errors.otherRequestedTermLimit
                        )}
                      />
                    </Flex>
                    {Boolean(
                      touched.requestedTermLimit && errors.requestedTermLimit
                    ) && (
                      <FormMessage invalid>
                        {errors.requestedTermLimit}
                      </FormMessage>
                    )}
                  </Box>
                  <Box marginBottom="16px">
                    <Box>
                      <Text
                        kind="body-sm-title"
                        fontWeight={theme.fontWeights.semibold}
                      >
                        Does your company plan to place more than one order with
                        a PO?
                      </Text>
                    </Box>
                    <Flex>
                      <Box marginRight="8px">
                        <Radio
                          scale="small"
                          id="multiplePurchaseOrders"
                          name="multiplePurchaseOrders"
                          value={'true'}
                          onChange={handleChange}
                          checked={values.multiplePurchaseOrders === 'true'}
                        />
                        <label htmlFor="yes">Yes</label>
                      </Box>
                      <Box>
                        <Radio
                          scale="small"
                          id="multiplePurchaseOrders"
                          name="multiplePurchaseOrders"
                          onChange={handleChange}
                          value={'false'}
                          checked={values.multiplePurchaseOrders === 'false'}
                        />
                        <label htmlFor="no">No</label>
                      </Box>
                    </Flex>
                    {Boolean(
                      touched.multiplePurchaseOrders &&
                        errors.multiplePurchaseOrders
                    ) && (
                      <FormMessage invalid>
                        {errors.multiplePurchaseOrders}
                      </FormMessage>
                    )}
                  </Box>
                  <Box marginBottom="16px">
                    <Box>
                      <Text
                        kind="body-sm-title"
                        fontWeight={theme.fontWeights.semibold}
                      >
                        Financial Documents
                      </Text>
                    </Box>
                    <Text kind="body-sm">
                      Financial documents from the last 30 days will assist in
                      the process of establishing the amount of credit that can
                      be extended to you. Examples may include: current bank
                      statements, PNLs, recent funding documents, credit packet,
                      or Tax Exemption Letters. Provide as many as apply.
                    </Text>
                  </Box>
                  <Box marginBottom="28px">
                    <FileUpload
                      fileList={fileList}
                      accept={['.pdf', '.png', '.jpg', '.jpeg']}
                      sizeLimit={1024000}
                      onFileSelected={(selectedFiles) => {
                        const newFileList = [...fileList, ...selectedFiles];
                        setFileList(newFileList);
                      }}
                    />
                    <FilePreviewList
                      fileList={fileList}
                      onFileListChange={(fileListChange) => {
                        setFileList([...fileListChange]);
                      }}
                      hideRemove={false}
                    ></FilePreviewList>
                  </Box>
                </Box>
                {/* Authorized Signature Section */}
                <Box
                  borderBottom={`1px solid ${theme.colors.darkBlues[25]}`}
                  marginBottom="28px"
                >
                  <Box marginBottom="16px">
                    <Text kind="body-title">Authorized Signature</Text>
                  </Box>
                  <Box marginBottom="16px">
                    <Flex>
                      <Box paddingRight={theme.space[1]}>
                        <Checkbox
                          scale="small"
                          disabled={isSubmitting}
                          onChange={handleChange}
                          data-testid="financialDocs"
                          id="financialDocs"
                          name="financialDocs"
                          type="checkbox"
                          invalid={Boolean(
                            touched.financialDocs && errors.financialDocs
                          )}
                        />
                      </Box>
                      <Box marginTop="1px">
                        <Text kind="body-sm" htmlFor="financialDocs">
                          By checking this box, I confirm that this information
                          is correct and that I have provided proper financial
                          documentation and the{' '}
                          <Link
                            target="_blank"
                            href="https://xometry-marketing.cdn.prismic.io/xometry-marketing/1118dbc9-4613-49ca-9787-12c20a923304_Xometry+Customer+Service+Agreement+%28Buyer+Terms%29+FINAL+2021.11.15.pdf"
                          >
                            Terms of Use
                          </Link>
                          .
                        </Text>
                      </Box>
                    </Flex>
                    {Boolean(touched.financialDocs && errors.financialDocs) && (
                      <FormMessage invalid>{errors.financialDocs}</FormMessage>
                    )}
                  </Box>
                  <Flex marginBottom="16px" width="70%">
                    <Box marginRight="8px" width="100%">
                      <Label htmlFor="applicantName">Applicant Name</Label>
                      <Input
                        width="100%"
                        className="pii-encrypt"
                        id="applicantName"
                        value={values.applicantName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={Boolean(
                          touched.applicantName && errors.applicantName
                        )}
                      />
                      {Boolean(
                        touched.applicantName && errors.applicantName
                      ) && (
                        <FormMessage invalid>
                          {errors.applicantName}
                        </FormMessage>
                      )}
                    </Box>
                    <Box width="100%">
                      <Label htmlFor="applicantTitle">Applicant Title</Label>
                      <Input
                        width="100%"
                        className="pii-encrypt"
                        id="applicantTitle"
                        value={values.applicantTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={Boolean(
                          touched.applicantTitle && errors.applicantTitle
                        )}
                      />
                      {Boolean(
                        touched.applicantTitle && errors.applicantTitle
                      ) && (
                        <FormMessage invalid>
                          {errors.applicantTitle}
                        </FormMessage>
                      )}
                    </Box>
                  </Flex>
                  <Box marginBottom="28px" width="70%">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      width="49%"
                      className="pii-encrypt"
                      id="date"
                      value={values.date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={Boolean(touched.date && errors.date)}
                    />
                    {Boolean(touched.date && errors.date) && (
                      <FormMessage invalid>{errors.date}</FormMessage>
                    )}
                  </Box>
                </Box>
                {/* Authorized Users Section */}
                {/* Temporarily commenting out this section */}
                {/* <AuthorizedEmails
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  values={values}
                  errors={errors}
                  currentInput={currentInput}
                  setCurrentInput={setCurrentInput}
                /> */}
                <Flex justifyContent="flex-end" marginTop="24px">
                  <Button
                    kind={theme.kind.primary}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Submit
                  </Button>
                </Flex>
              </form>
            )}
          </Formik>
        </Panel>
      )}
    </>
  );
};

export default CreditTermsForm;
